import React, { useState, useEffect } from 'react';
import superagent from 'superagent';
import { 
  Dialog,
  DialogContent 
} from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Input } from '@shadcn/components/ui/input';
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shadcn/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shadcn/components/ui/tooltip';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Power, 
  PowerOff, 
  Save,
  X,
  CheckCircle,
  Circle
} from 'lucide-react';
import { backendUrl } from '@helpers/consts';

const AlertConfigModal = ({ isOpen, onClose, onConfigActivated }) => {
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    accuracyThresholds: {
      critical: 70,
      high: 80,
      medium: 85,
      low: 90
    },
    varianceThresholds: {
      critical: 1000,
      high: 500,
      medium: 250,
      low: 100
    },
    toleranceBreachThresholds: {
      critical: 0.50,
      high: 0.30,
      medium: 0.20,
      low: 0.10
    },
   
  });

  useEffect(() => {
    if (isOpen) {
      fetchConfigs();
      fetchActiveConfig();
    }
  }, [isOpen]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await superagent
        .get(`${backendUrl}/alert-config`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      setConfigs(response.body || []);
    } catch (error) {
      console.error('Error fetching configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveConfig = async () => {
    try {
      const response = await superagent
        .get(`${backendUrl}/alert-config/active`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      setActiveConfig(response.body);
    } catch (error) {
      console.error('Error fetching active config:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingConfig(null);
    setFormData({
      name: '',
      description: '',
      accuracyThresholds: {
        critical: 70,
        high: 80,
        medium: 85,
        low: 90
      },
      varianceThresholds: {
        critical: 1000,
        high: 500,
        medium: 250,
        low: 100
      },
      toleranceBreachThresholds: {
        critical: 0.50,
        high: 0.30,
        medium: 0.20,
        low: 0.10
      },
     
    });
    setActiveTab('form');
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      description: config.description,
      accuracyThresholds: { ...config.accuracyThresholds },
      varianceThresholds: { ...config.varianceThresholds },
      toleranceBreachThresholds: { ...config.toleranceBreachThresholds },
      
    });
    setActiveTab('form');
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const url = editingConfig 
        ? `${backendUrl}/alert-config/${editingConfig._id}`
        : `${backendUrl}/alert-config`;
      
      const method = editingConfig ? 'put' : 'post';
      
      await superagent[method](url)
        .send(formData)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      
      await fetchConfigs();
      await fetchActiveConfig();
      setActiveTab('list');
      setEditingConfig(null);
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (configId) => {
    try {
      setLoading(true);
      await superagent
        .put(`${backendUrl}/alert-config/${configId}/activate`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);

      await fetchConfigs();
      await fetchActiveConfig();

      // Call callback to refresh alerts data instead of reloading page
      if (onConfigActivated) {
        onConfigActivated();
      }
    } catch (error) {
      console.error('Error activating config:', error);
      alert('Failed to activate configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (configId) => {
    try {
      setLoading(true);
      await superagent
        .put(`${backendUrl}/alert-config/${configId}/deactivate`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      
      await fetchConfigs();
      await fetchActiveConfig();
    } catch (error) {
      console.error('Error deactivating config:', error);
      alert('Failed to deactivate configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (config) => {
    if (config.active) {
      alert('Cannot delete active configuration');
      return;
    }
    
    

    try {
      setLoading(true);
      await superagent
        .delete(`${backendUrl}/alert-config/${config._id}`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      
      await fetchConfigs();
    } catch (error) {
      console.error('Error deleting config:', error);
      alert('Failed to delete configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateFormField = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Custom NumberInput component to prevent focus loss
  const NumberInput = ({ value, onChange, unit, className = "", ...props }) => {
    const [localValue, setLocalValue] = useState(value);
    
    // Update local value when prop value changes (e.g. when form is reset)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);
    
    const handleChange = (e) => {
      const newValue = e.target.value;
      // Allow empty string or valid numbers
      if (newValue === "" || /^[0-9]*\.?[0-9]*$/.test(newValue)) {
        setLocalValue(newValue);
      }
    };
    
    const handleBlur = () => {
      // Convert to number and update parent state only on blur
      const numValue = parseFloat(localValue);
      if (!isNaN(numValue)) {
        onChange(numValue);
      } else if (localValue === "") {
        onChange(0); // Default to 0 for empty values
      }
    };
    
    return (
      <div className="relative">
        <Input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`pr-8 ${className}`}
          {...props}
        />
        {unit && <span className="absolute right-3 top-2 text-xs text-gray-500">{unit}</span>}
      </div>
    );
  };

  // Special component for tolerance breach thresholds (display as % but store as decimal)
  const ToleranceThresholdGroup = ({ title, thresholds, pathPrefix }) => (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(thresholds).map(([level, value]) => (
          level !== '_id' && (
          <div key={level} className="space-y-2">
            <label className="block text-xs font-medium text-gray-700 capitalize">
              {level}
            </label>
            <NumberInput
              value={value * 100} // Display as percentage
              onChange={(displayValue) => updateFormField(`${pathPrefix}.${level}`, displayValue / 100)} // Store as decimal
              unit="%"
            />
          </div>
          )
        ))}
      </div>
    </div>
  );

  const ThresholdGroup = ({ title, thresholds, pathPrefix, unit = '%' }) => (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(thresholds).map(([level, value]) => (
          level !== '_id' && (
          <div key={level} className="space-y-2">
            <label className="block text-xs font-medium text-gray-700 capitalize">
              {level}
            </label>
            <NumberInput
              value={value}
              onChange={(value) => updateFormField(`${pathPrefix}.${level}`, value)}
              unit={unit}
            />
          </div>
          )
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-y-auto">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-900 rounded-full">
                <Settings className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Alert Configuration</h2>
                <p className="text-sm text-gray-600">Manage alert detection settings</p>
              </div>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              {/* Tab Navigation */}
              <div className="px-6 py-4 border-b border-gray-100">
                <TabsList className="bg-gray-100 p-1 rounded-xl">
                  <TabsTrigger 
                    value="list" 
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 text-sm font-medium"
                  >
                    Configurations
                  </TabsTrigger>
                  <TabsTrigger 
                    value="form" 
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2 text-sm font-medium"
                  >
                    {editingConfig ? 'Edit Config' : 'New Config'}
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-auto">
                <TabsContent value="list" className="p-0">
                  <div className="p-6 space-y-6">
                    {/* Active Config Summary */}
                    {activeConfig && (
                      <Card className="border-2 border-green-200 bg-green-50 rounded-2xl">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                              <div>
                                <h3 className="font-semibold text-green-900">Active Configuration</h3>
                                <p className="text-sm text-green-700">{activeConfig.name}</p>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleEdit(activeConfig)}
                              variant="outline"
                              className="border-green-300 text-green-700 hover:bg-green-100 rounded-lg"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Create New Button */}
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-gray-900">All Configurations</h3>
                      <Button
                        onClick={handleCreateNew}
                        className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Configuration
                      </Button>
                    </div>

                    {/* Configurations List */}
                    {loading ? (
                      <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {configs.map((config) => (
                          <Card key={config._id} className={`rounded-2xl transition-all ${
                            config.active 
                              ? 'border-2 border-green-200 shadow-md' 
                              : 'border border-gray-200 hover:shadow-md'
                          }`}>
                            <CardContent className="p-6">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="flex items-center space-x-2">
                                    {config.active ? (
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <Circle className="h-5 w-5 text-gray-400" />
                                    )}
                                    <div>
                                      <h4 className="font-semibold text-gray-900">{config.name}</h4>
                                      <p className="text-sm text-gray-600">{config.description}</p>
                                    </div>
                                  </div>
                                  {config.active && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                      Active
                                    </span>
                                  )}
                                </div>

                                <TooltipProvider>
                                  <div className="flex items-center space-x-2">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          onClick={() => handleEdit(config)}
                                          variant="ghost"
                                          className="h-9 w-9 p-0 rounded-full hover:bg-gray-100"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Edit configuration</TooltipContent>
                                    </Tooltip>

                                    {config.active ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            onClick={() => handleDeactivate(config._id)}
                                            variant="ghost"
                                            className="h-9 w-9 p-0 rounded-full hover:bg-red-100 text-red-600"
                                          >
                                            <PowerOff className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Deactivate</TooltipContent>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            onClick={() => handleActivate(config._id)}
                                            variant="ghost"
                                            className="h-9 w-9 p-0 rounded-full hover:bg-green-100 text-green-600"
                                          >
                                            <Power className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Activate</TooltipContent>
                                      </Tooltip>
                                    )}

                                    {!config.active && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            onClick={() => handleDelete(config)}
                                            variant="ghost"
                                            className="h-9 w-9 p-0 rounded-full hover:bg-red-100 text-red-600"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Delete configuration</TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TooltipProvider>
                              </div>

                              {/* Quick Preview */}
                              <div className="mt-4 grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
                                <div className="text-center">
                                  <p className="text-xs text-gray-600">Critical Accuracy</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {config.accuracyThresholds.critical}%
                                  </p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-gray-600">Critical Variance</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    ${config.varianceThresholds.critical}
                                  </p>
                                </div>
                            
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        {configs.length === 0 && (
                          <div className="text-center py-12">
                            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations</h3>
                            <p className="text-gray-600 mb-4">Create your first alert configuration</p>
                            <Button
                              onClick={handleCreateNew}
                              className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Create Configuration
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="form" className="p-0">
                  <div className="p-6 space-y-6">
                    {/* Basic Info */}
                    <Card className="rounded-2xl border border-gray-200">
                      <CardContent className="p-6 space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <Input
                              type="text"
                              value={formData.name}
                              onChange={(e) => updateFormField('name', e.target.value)}
                              placeholder="Production Config"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <Input
                              type="text"
                              value={formData.description}
                              onChange={(e) => updateFormField('description', e.target.value)}
                              placeholder="Configuration description"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Thresholds */}
                    <Card className="rounded-2xl border border-gray-200">
                      <CardContent className="p-6 space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900">Threshold Settings</h3>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <ThresholdGroup
                            title="Accuracy Thresholds"
                            thresholds={formData.accuracyThresholds}
                            pathPrefix="accuracyThresholds"
                            unit="%"
                          />
                          
                          <ThresholdGroup
                            title="Variance Thresholds"
                            thresholds={formData.varianceThresholds}
                            pathPrefix="varianceThresholds"
                            unit="$"
                          />
                        </div>

                        <ToleranceThresholdGroup
                          title="Tolerance Breach Thresholds"
                          thresholds={formData.toleranceBreachThresholds}
                          pathPrefix="toleranceBreachThresholds"
                        />
                      </CardContent>
                    </Card>

                    {/* Trend Analysis */}
                  

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                      <Button
                        onClick={() => setActiveTab('list')}
                        variant="outline"
                        className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={loading || !formData.name.trim()}
                        className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg disabled:opacity-50"
                      >
                        {loading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        {editingConfig ? 'Update' : 'Create'} Configuration
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AlertConfigModal;
